// A real-AI steering driver for a harness client's `drive` hook: it runs the
// core path-checker AI so the test opponent actually avoids walls and trails
// (a sine wobble just drives into things, which is useless for death testing).
//
// Subtlety: the AI needs the collision grid to see other worms' trails, but the
// client's gameState has the grid stripped on the wire (wireGameState sends an
// empty grid). So the bot reads the SERVER's authoritative world to DECIDE. That's
// fine - it's a test opponent, and its inputs still flow through the full client
// prediction -> simulated network -> server pipeline exactly like a human's. It
// just isn't blind to trails the way a grid-less client bot would be.
import PathCheckerAI from "core/src/ai/path-checker-ai-handler.js";
import * as gsf from "core/src/core/game-state-functions.js";

// `getWorld` returns the authoritative server gameState (with grid). The bot's own
// playerId arrives via the drive hook (assigned after connect). `skill` 1..10 tunes
// lookahead/replanning (see the AI).
export function createAiDriver(getWorld: () => any, skill = 9) {
  const ai = PathCheckerAI();
  let lastCall = 0;

  return function drive(_clientGs: any, _tSec: number, playerId: string | null): number {
    const world = getWorld();
    if (!world || !playerId) return 0;
    const player = world.players && world.players.find((p: any) => p.id === playerId);
    // Twin-aware: keep steering while ANY of the player's worms is alive, not just the
    // first (which may be dead while its twin lives). Core's AI plans for getAliveWorms[0],
    // so as worms die it steers for a survivor instead of giving up (returning 0 = straight
    // = the stranded twin drives into a wall, which looked "broken" when testing twin).
    if (!player || gsf.getAliveWorms(world, playerId).length === 0) return 0;

    const now = Date.now();
    const dt = lastCall ? Math.min(0.05, (now - lastCall) / 1000) : 0.016;
    lastCall = now;

    if (!player.aiData) player.aiData = { skill };
    const prevSteering = player.steering;
    try {
      ai.update(world, dt, player); // sets player.steering via setPlayerSteering
    } catch {
      return prevSteering || 0;
    }
    const s = player.steering; // -1 | 0 | 1
    // Don't disturb the server's own copy of steering (harmless for an externally-
    // controlled worm, but keep it clean): restore it and return the decision.
    gsf.setPlayerSteering(world, playerId, prevSteering || 0);
    return s;
  };
}
